import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight } from 'react-icons/fa';
import Container from '../components/ui/Container';
import CTASection from '../components/ui/CTASection';
import PageHero from '../components/ui/PageHero';
import { serviceCategories } from '../data/services';

export default function ServicesPage() {
  const [activeCategory, setActiveCategory] = useState(null);

  const displayed = activeCategory
    ? serviceCategories.filter(c => c.id === activeCategory)
    : serviceCategories;

  return (
    <main className="w-full overflow-x-hidden pt-16 lg:pt-20">
      <PageHero
        badge="Our Services"
        title="Comprehensive Business Solutions"
        subtitle="From finance and legal to compliance and technology — explore our complete range of professional services."
      />

      {/* Category Filter */}
      <section className="w-full py-4 lg:py-6 sticky top-16 lg:top-20 z-40" style={{ background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border-light)', backdropFilter: 'blur(10px)' }}>
        <Container>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
            <button
              onClick={() => setActiveCategory(null)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${!activeCategory ? 'text-white' : 'border'}`}
              style={!activeCategory ? { background: 'var(--gradient-primary)' } : { borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
            >
              All Services
            </button>
            {serviceCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${activeCategory === cat.id ? 'text-white' : 'border'}`}
                style={activeCategory === cat.id ? { background: 'var(--gradient-primary)' } : { borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
              >
                {cat.title}
              </button>
            ))}
          </div>
        </Container>
      </section>

      {/* Services Grid */}
      <section className="section-pad section-bg-alt">
        <Container>
          {displayed.map(category => (
            <div key={category.id} id={category.id} className="mb-16 last:mb-0">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-4 mb-8"
              >
                <div
                  className="w-1 h-10 rounded-full flex-shrink-0"
                  style={{ background: 'var(--gradient-gold)' }}
                />
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--color-text)' }}>
                    {category.title}
                  </h2>
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>{category.description}</p>
                </div>
              </motion.div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {category.services.map((service, i) => (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    whileHover={{ y: -3 }}
                    className="card p-5 flex flex-col group"
                  >
                    <h3 className="font-semibold mb-2 text-sm md:text-base lg:text-lg" style={{ color: 'var(--color-text)' }}>
                      {service.title}
                    </h3>
                    <p className="text-xs lg:text-sm leading-relaxed flex-1 mb-4" style={{ color: 'var(--color-text-muted)' }}>
                      {service.description}
                    </p>
                    <Link
                      to={`/services/${service.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs lg:text-sm font-semibold group-hover:gap-2.5 transition-all duration-200"
                      style={{ color: 'var(--color-primary)' }}
                    >
                      Know More <FaArrowRight className="text-xs" />
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </Container>
      </section>

      <CTASection title="Need Help Choosing a Service?" subtitle="Talk to our experts for a free consultation and we'll guide you to the right solution." />
    </main>
  );
}
